import { type DeltaListener } from './deltas/types';
import { type SelectionEvent } from './events/types';
import { selectionsReducer } from './reducers/selections-reducer';
import {
  type FlattenedSelection,
  type SelectionRange,
  type SelectionState,
} from './state/types';
import { createInitialSelectionState } from './utils/create-initial-selection-state';

/**
 * SelectionMachine manages the state of cell, row, and column selections in a database table.
 *
 * This state machine handles all selection-related operations including:
 * - Single and multi-cell selections
 * - Row and column selections
 * - Range selections (rectangular blocks via anchor/lead)
 * - Keyboard navigation and focus management
 * - Selection clearing and toggling
 *
 * The machine uses an event-driven architecture where selection changes are dispatched
 * as events, processed by reducers, and result in state updates with corresponding deltas
 * that describe what changed. Listeners can subscribe to these deltas to react to
 * selection changes (e.g., updating UI highlights, notifying other systems).
 *
 * @example
 * ```ts
 * const machine = new SelectionMachine();
 *
 * // Subscribe to selection changes
 * const unsubscribe = machine.subscribe((delta, state) => {
 *   if (delta.kind === 'selectionModified') {
 *     console.log('Selections updated:', state.selections);
 *   }
 * });
 *
 * // Select a cell
 * machine.dispatch({
 *   type: 'selectCell',
 *   cell: {
 *     row: { index: 0 },
 *     col: { index: 0 },
 *   },
 *   source: 'mouse',
 * });
 *
 * // Navigate with keyboard
 * machine.dispatch({
 *   type: 'focusCell',
 *   cell: {
 *     row: { index: 0 },
 *     col: { index: 1 },
 *   },
 *   source: 'keyboard',
 * });
 * ```
 */
export class SelectionMachine {
  #state: SelectionState;
  #listeners = new Set<DeltaListener>();

  /**
   * Creates a new SelectionMachine instance.
   *
   * @param initial - Optional initial state. If not provided, creates an empty state
   *                  with no selections or focus.
   */
  constructor(initial?: SelectionState) {
    this.#state = initial ?? createInitialSelectionState();
  }

  /**
   * Returns the current selection state.
   *
   * @returns The current SelectionState including selections array, focusedCell,
   *          and interaction metadata.
   */
  getState() {
    return this.#state;
  }

  /**
   * Flattens all selection ranges into concrete index values.
   *
   * Takes the compact anchor/lead representation of each selection range
   * and expands it into the full list of indices covered. The shape of
   * the returned values depends on the current mode:
   *
   * - `idle`: No selections, returns `null`
   * - `cell`: Returns `{ row, col }[]` for every cell in all ranges
   * - `row`: Returns `number[]` of all row indices
   * - `column`: Returns `number[]` of all column indices
   *
   * @returns A discriminated union with `mode` and the flattened `values`
   *
   * @example
   * ```ts
   * // Given a cell selection from (0,0) to (2,1):
   * const result = machine.getFlattenedSelection();
   * // result.mode === 'cell'
   * // result.values === [
   * //   { row: 0, col: 0 }, { row: 0, col: 1 },
   * //   { row: 1, col: 0 }, { row: 1, col: 1 },
   * //   { row: 2, col: 0 }, { row: 2, col: 1 },
   * // ]
   * ```
   */
  getFlattenedSelection(): FlattenedSelection {
    if (this.#state.mode === 'idle' || this.#state.selections.length === 0) {
      return { mode: 'idle', values: null };
    }

    if (this.#state.mode === 'cell') {
      return {
        mode: 'cell',
        values: this.#flattenCellSelection(this.#state.selections[0]),
      };
    }

    if (this.#state.mode === 'row') {
      return {
        mode: 'row',
        values: this.#flattenRowSelection(this.#state.selections),
      };
    }

    if (this.#state.mode === 'column') {
      return {
        mode: 'column',
        values: this.#flattenColumnSelection(this.#state.selections),
      };
    }

    return { mode: 'idle', values: null };
  }

  #flattenCellSelection(selection: SelectionRange): { row: number; col: number }[] {
    const values: { row: number; col: number }[] = [];

    // Compute the bounding box of the selection.
    // Using min/max handles "reverse" selections where lead < anchor.
    const minRow = Math.min(selection.anchor.row.index, selection.lead.row.index);
    const maxRow = Math.max(selection.anchor.row.index, selection.lead.row.index);
    const minCol = Math.min(selection.anchor.col.index, selection.lead.col.index);
    const maxCol = Math.max(selection.anchor.col.index, selection.lead.col.index);

    // Fill in every cell within the rectangular region
    for (let row = minRow; row <= maxRow; row++) {
      for (let col = minCol; col <= maxCol; col++) {
        values.push({ row, col });
      }
    }

    return values;
  }

  #flattenRowSelection(selections: SelectionRange[]): number[] {
    // Row mode can have multiple selections (e.g., Cmd+Click to select rows 1, 3, 5).
    // We only care about row indices - column is ignored.
    const values: number[] = [];

    // Loop through each selection range
    for (const selection of selections) {
      // Get min/max row from anchor and lead
      const minRow = Math.min(selection.anchor.row.index, selection.lead.row.index);
      const maxRow = Math.max(selection.anchor.row.index, selection.lead.row.index);

      // Push every row index from min to max into the result
      for (let row = minRow; row <= maxRow; row++) {
        values.push(row);
      }
    }

    return values;
  }

  #flattenColumnSelection(selections: SelectionRange[]): number[] {
    // Column mode can have multiple selections (e.g., Cmd+Click to select columns A, C, E).
    // We only care about column indices - row is ignored.
    const values: number[] = [];

    // Loop through each selection range
    for (const selection of selections) {
      // Get min/max col from anchor and lead
      const minCol = Math.min(selection.anchor.col.index, selection.lead.col.index);
      const maxCol = Math.max(selection.anchor.col.index, selection.lead.col.index);

      // Push every column index from min to max into the result
      for (let col = minCol; col <= maxCol; col++) {
        values.push(col);
      }
    }

    return values;
  }

  /**
   * Subscribes to selection change notifications.
   *
   * The listener will be called whenever a selection event is dispatched that
   * results in a state change (i.e., when the delta is not 'noop'). The listener
   * receives both the delta describing what changed and the new state.
   *
   * @param listener - Callback function that receives (delta, state) when changes occur
   * @returns Unsubscribe function to remove this listener
   *
   * @example
   * ```ts
   * const unsubscribe = machine.subscribe((delta, state) => {
   *   console.log('Selection changed:', delta.kind);
   * });
   *
   * // Later, to stop listening:
   * unsubscribe();
   * ```
   */
  subscribe(listener: DeltaListener) {
    this.#listeners.add(listener);

    return () => this.#listeners.delete(listener);
  }

  /**
   * Dispatches a selection event to update the state.
   *
   * The event is processed by the reducer, which returns both the new state
   * and a delta describing what changed. If the delta is not 'noop' (meaning
   * something actually changed), all registered listeners are notified.
   *
   * @param event - The selection event to process (e.g., selectCell, extendToCell,
   *                deselectAll, focusCell, etc.)
   *
   * @example
   * ```ts
   * // Select a cell
   * machine.dispatch({
   *   type: 'selectCell',
   *   cell: {
   *     row: { index: 0 },
   *     col: { index: 0 },
   *   },
   *   source: 'mouse',
   * });
   *
   * // Clear all selections
   * machine.dispatch({
   *   type: 'deselectAll',
   *   source: 'keyboard',
   * });
   * ```
   */
  dispatch(event: SelectionEvent) {
    const { next, delta } = selectionsReducer(this.#state, event);

    this.#state = next;

    if (delta.kind !== 'noop') {
      for (const listener of this.#listeners) {
        listener(delta, next);
      }
    }
  }
}
